/**
 * Access Level ↔ Permission Mapping
 *
 * Maps each permission group to specific permissions at each access level.
 * Access levels are cumulative: EDIT includes VIEW, ADMIN includes EDIT + VIEW.
 *
 * @see Issue: Role Access Configuration
 */

import { AccessLevel } from '../enums/access-level';
import { Permission, type PermissionValue, USER_ROLE_PERMISSIONS } from '../enums/permissions';
import { PERMISSION_GROUP_ORDER, PERMISSION_GROUPS } from './permission-groups';

/**
 * For each permission group, defines which permissions belong to each access level.
 * Only non-cumulative permissions are listed — use getPermissionsForAccessLevel() for cumulative.
 */
export const PERMISSION_GROUP_ACCESS_LEVELS: Record<
  string,
  Partial<Record<AccessLevel.VIEW | AccessLevel.EDIT | AccessLevel.ADMIN, PermissionValue[]>>
> = {
  LOAN_OPERATIONS: {
    [AccessLevel.VIEW]: [Permission.loan.service],
    [AccessLevel.EDIT]: [Permission.loan.originate, Permission.loan.process, Permission.loan.modify],
    [AccessLevel.ADMIN]: [Permission.loan.underwrite, Permission.loan.fund, Permission.loan.approveModification],
  },

  CONSUMER_DATA: {
    [AccessLevel.VIEW]: [Permission.consumer.financialsView],
    [AccessLevel.EDIT]: [Permission.consumer.piiAccess],
    [AccessLevel.ADMIN]: [Permission.consumer.creditPull, Permission.consumer.impersonate],
  },

  DOCUMENTS: {
    [AccessLevel.VIEW]: [Permission.documents.read],
    [AccessLevel.EDIT]: [Permission.documents.upload, Permission.documents.analyze, Permission.documents.verify],
    [AccessLevel.ADMIN]: [
      Permission.documents.delete,
      Permission.documents.sensitiveRead,
      Permission.documents.restrictedRead,
    ],
  },

  PIPELINE: {
    [AccessLevel.VIEW]: [Permission.pipeline.stageTransition],
    [AccessLevel.EDIT]: [Permission.pipeline.amountOverride],
    [AccessLevel.ADMIN]: [Permission.pipeline.dualControlApprove],
  },

  AGENTS: {
    [AccessLevel.VIEW]: [Permission.agents.read],
    [AccessLevel.EDIT]: [Permission.agents.create, Permission.agents.update],
    [AccessLevel.ADMIN]: [Permission.agents.delete, Permission.agents.deploy],
  },

  CONVERSATIONS: {
    [AccessLevel.VIEW]: [Permission.conversations.read, Permission.messages.read],
    [AccessLevel.EDIT]: [Permission.conversations.create, Permission.messages.create],
    [AccessLevel.ADMIN]: [Permission.conversations.delete],
  },

  DOCUMENT_INTELLIGENCE: {
    [AccessLevel.VIEW]: [Permission.rulesets.read, Permission.findings.read],
    [AccessLevel.EDIT]: [Permission.rulesets.create, Permission.rulesets.update, Permission.findings.update],
    [AccessLevel.ADMIN]: [Permission.rulesets.delete],
  },

  KNOWLEDGE_BASE: {
    [AccessLevel.VIEW]: [Permission.knowledgeBase.read],
    [AccessLevel.EDIT]: [Permission.knowledgeBase.create, Permission.knowledgeBase.update],
    [AccessLevel.ADMIN]: [Permission.knowledgeBase.delete],
  },

  USER_MANAGEMENT: {
    [AccessLevel.VIEW]: [Permission.users.read],
    [AccessLevel.EDIT]: [Permission.users.create, Permission.users.update],
    [AccessLevel.ADMIN]: [Permission.users.delete],
  },

  TEAM_MANAGEMENT: {
    [AccessLevel.VIEW]: [Permission.teams.manageMembers],
    [AccessLevel.EDIT]: [Permission.teams.create, Permission.teams.update],
    [AccessLevel.ADMIN]: [Permission.teams.delete],
  },

  ENTITY_FRAMEWORK: {
    [AccessLevel.VIEW]: [Permission.entities.read],
    [AccessLevel.EDIT]: [
      Permission.entities.create,
      Permission.entities.update,
      Permission.entities.fields.create,
      Permission.entities.fields.update,
      Permission.entities.fields.reorder,
      Permission.entities.relationships.create,
    ],
    [AccessLevel.ADMIN]: [
      Permission.entities.delete,
      Permission.entities.fields.delete,
      Permission.entities.relationships.delete,
    ],
  },

  PARTNER_MANAGEMENT: {
    [AccessLevel.VIEW]: [Permission.partners.read, Permission.partners.agreements.read],
    [AccessLevel.EDIT]: [
      Permission.partners.invite,
      Permission.partners.refer,
      Permission.partners.agreements.create,
      Permission.partners.agreements.update,
    ],
    [AccessLevel.ADMIN]: [
      Permission.partners.manage,
      Permission.partners.delete,
      Permission.partners.agreements.approve,
    ],
  },

  API_INTEGRATIONS: {
    [AccessLevel.VIEW]: [Permission.apiKeys.read, Permission.integrations.read],
    [AccessLevel.EDIT]: [Permission.apiKeys.create, Permission.integrations.create, Permission.integrations.update],
    [AccessLevel.ADMIN]: [Permission.apiKeys.revoke, Permission.integrations.delete],
  },

  SETTINGS: {
    [AccessLevel.VIEW]: [Permission.settings.read],
    [AccessLevel.EDIT]: [Permission.settings.update],
    [AccessLevel.ADMIN]: [],
  },

  ANALYTICS: {
    [AccessLevel.VIEW]: [Permission.analytics.read, Permission.audit.read],
    [AccessLevel.EDIT]: [Permission.analytics.export, Permission.audit.export],
    [AccessLevel.ADMIN]: [],
  },

  BILLING: {
    [AccessLevel.VIEW]: [Permission.billing.read],
    [AccessLevel.EDIT]: [Permission.billing.update],
    [AccessLevel.ADMIN]: [],
  },

  NOTIFICATIONS: {
    [AccessLevel.VIEW]: [
      Permission.notifications.read,
      Permission.notifications.rules.read,
      Permission.notifications.templates.read,
    ],
    [AccessLevel.EDIT]: [
      Permission.notifications.create,
      Permission.notifications.update,
      Permission.notifications.rules.create,
      Permission.notifications.rules.update,
      Permission.notifications.templates.create,
      Permission.notifications.templates.update,
    ],
    [AccessLevel.ADMIN]: [
      Permission.notifications.readAll,
      Permission.notifications.delete,
      Permission.notifications.rules.delete,
      Permission.notifications.templates.delete,
    ],
  },
};

/**
 * Get all permissions granted at a specific access level (cumulative).
 * EDIT includes VIEW permissions, ADMIN includes EDIT + VIEW.
 *
 * @param groupKey - The permission group key (e.g., 'PARTNER_MANAGEMENT')
 * @param level - The access level
 * @returns Array of permission values granted at this level
 */
export function getPermissionsForAccessLevel(groupKey: string, level: AccessLevel): PermissionValue[] {
  if (level === AccessLevel.NONE) return [];

  const groupConfig = PERMISSION_GROUP_ACCESS_LEVELS[groupKey];
  if (!groupConfig) return [];

  const permissions: PermissionValue[] = [];

  // Cumulative: VIEW always included
  if (groupConfig[AccessLevel.VIEW]) {
    permissions.push(...groupConfig[AccessLevel.VIEW]);
  }

  // EDIT and ADMIN include EDIT-level permissions
  if (level === AccessLevel.EDIT || level === AccessLevel.ADMIN) {
    if (groupConfig[AccessLevel.EDIT]) {
      permissions.push(...groupConfig[AccessLevel.EDIT]);
    }
  }

  // ADMIN includes all permissions
  if (level === AccessLevel.ADMIN) {
    if (groupConfig[AccessLevel.ADMIN]) {
      permissions.push(...groupConfig[AccessLevel.ADMIN]);
    }
  }

  return permissions;
}

/**
 * Derive the effective access level from a set of permissions for a given group.
 * Checks from highest to lowest and returns the highest level the permission set satisfies.
 *
 * @param groupKey - The permission group key
 * @param permissions - The user's current permissions (Set or array)
 * @returns The derived access level
 */
export function deriveAccessLevel(groupKey: string, permissions: ReadonlySet<string> | string[]): AccessLevel {
  const permSet = permissions instanceof Set ? permissions : new Set(permissions);

  // Wildcard means admin on everything
  if (permSet.has(Permission.all)) return AccessLevel.ADMIN;

  const groupConfig = PERMISSION_GROUP_ACCESS_LEVELS[groupKey];
  if (!groupConfig) return AccessLevel.NONE;

  // Check ADMIN level — must have all VIEW + EDIT + ADMIN permissions
  const adminPerms = getPermissionsForAccessLevel(groupKey, AccessLevel.ADMIN);
  if (adminPerms.length > 0 && adminPerms.every((p) => permSet.has(p))) {
    return AccessLevel.ADMIN;
  }

  // Check EDIT level — must have all VIEW + EDIT permissions
  const editPerms = getPermissionsForAccessLevel(groupKey, AccessLevel.EDIT);
  if (editPerms.length > 0 && editPerms.every((p) => permSet.has(p))) {
    return AccessLevel.EDIT;
  }

  // Check VIEW level — must have all VIEW permissions
  const viewPerms = getPermissionsForAccessLevel(groupKey, AccessLevel.VIEW);
  if (viewPerms.length > 0 && viewPerms.every((p) => permSet.has(p))) {
    return AccessLevel.VIEW;
  }

  return AccessLevel.NONE;
}

/**
 * Compute default access levels for a role from USER_ROLE_PERMISSIONS.
 *
 * @param role - The role string (e.g., 'business_user', 'business_viewer')
 * @returns Record of groupKey → AccessLevel
 */
export function getDefaultAccessLevels(role: string): Record<string, AccessLevel> {
  const normalizedRole = role.toLowerCase();
  const rolePermissions = USER_ROLE_PERMISSIONS[normalizedRole] || [];

  // Admin wildcard roles get ADMIN on everything
  if (rolePermissions.includes(Permission.all)) {
    const result: Record<string, AccessLevel> = {};
    for (const key of PERMISSION_GROUP_ORDER) {
      result[key] = AccessLevel.ADMIN;
    }
    return result;
  }

  const permSet = new Set<string>(rolePermissions);
  const result: Record<string, AccessLevel> = {};

  for (const key of PERMISSION_GROUP_ORDER) {
    result[key] = deriveAccessLevel(key, permSet);
  }

  return result;
}

/**
 * Get permission group metadata for UI display, ordered correctly.
 */
export function getPermissionGroupsForMatrix(): Array<{
  key: string;
  name: string;
  description: string;
  icon?: string;
  permissionCount: number;
}> {
  return PERMISSION_GROUP_ORDER.flatMap((key) => {
    const group = PERMISSION_GROUPS[key];
    if (!group) return [];
    return [
      {
        key,
        name: group.name,
        description: group.description,
        icon: group.icon,
        permissionCount: group.permissions.length,
      },
    ];
  });
}
